import React, { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import { MENU_ID_PROJECTS } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useCreateProjectCheckPermissions } from '@atlassian/jira-experiments-analytics/src/hooks/useCreateProjectCheckPermissions.tsx';
import type { Nav4DragAndDropMenuItemProps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { ActionButtons } from '../../common/ui/action-buttons/ActionButtons.tsx';
import type { Nav4Projects$key } from './__generated__/Nav4Projects.graphql';
import { Nav4ProjectsDefault } from './default/Nav4ProjectsDefault.tsx';
import { Nav4ProjectsForLanding } from './for-landing/Nav4ProjectsForLanding.tsx';

export type Nav4ProjectsProps = Nav4DragAndDropMenuItemProps & {
	isExpanded: boolean;
	queryRef: Nav4Projects$key;
};

export function Nav4Projects(props: Nav4ProjectsProps): React.JSX.Element {
	const { isExpanded, queryRef, ...dndProps } = props;
	const data = useFragment<Nav4Projects$key>(
		graphql`
			fragment Nav4Projects on Query
			@argumentDefinitions(
				isAllowingEndUsersToAccessCustomTemplates: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-custom-template-allow-end-users-to-create-custom-templates.relayprovider"
				}
			) {
				...Nav4ProjectsForLanding @include(if: $isProjectsExpanded)
				jira {
					hasIsProjectsExpanded: __typename @include(if: $isProjectsExpanded)
					canCreateProject: canPerform(type: CREATE_PROJECT, cloudId: $cloudId)
						@optIn(to: "JiraAction")
					shouldDisplay: canPerform(type: VIEW_PROJECT_CREATION_ENTRY, cloudId: $cloudId)
						@include(if: $isAllowingEndUsersToAccessCustomTemplates)
						@optIn(to: "JiraAction")
				}
			}
		`,
		queryRef,
	);

	const { isInitialSelectedPath } = useSidebarNav4();

	const canCreateProject = !!data?.jira?.canCreateProject;
	const shouldDisplayCreateProject = !!data?.jira?.shouldDisplay;
	const actionButtons = useMemo(
		() => (
			<ActionButtons
				canCreateProject={canCreateProject}
				{...(fg('allow_end_users_to_access_custom_templates')
					? { shouldDisplayCreateProject }
					: {})}
			/>
		),
		[canCreateProject, shouldDisplayCreateProject],
	);

	useCreateProjectCheckPermissions();

	const isExpandedValue = fg('navx-global-side-nav')
		? !!dndProps.isExpandOrFlyoutOpen && isExpanded
		: isExpanded;

	if (!data.jira?.hasIsProjectsExpanded && isInitialSelectedPath(MENU_ID_PROJECTS)) {
		// We need to make sure that we're only passing the conditionalal fragment when it is actually
		// available. Due to a lack of support the @alias Relay directive, we need to use another mechanism
		// and in this case we're using a hardcoded __typename to check if the fragment is available.
		// This is a workaround until we can use the @alias directive in Relay. If the hasIsProjectsExpanded
		// attribute is not available, then the fragment won't be either and we shouldn't pass it.
		return (
			<Nav4ProjectsDefault
				actionsOnHover={actionButtons}
				canCreateProject={canCreateProject}
				isExpanded={isExpandedValue}
				{...dndProps}
			/>
		);
	}

	return isInitialSelectedPath(MENU_ID_PROJECTS) ? (
		<Nav4ProjectsForLanding
			actionsOnHover={actionButtons}
			canCreateProject={canCreateProject}
			isExpanded={isExpandedValue}
			queryRef={data}
			{...dndProps}
		/>
	) : (
		<Nav4ProjectsDefault
			actionsOnHover={actionButtons}
			canCreateProject={canCreateProject}
			isExpanded={isExpanded}
			{...dndProps}
		/>
	);
}
